/**
 * Dates and clock times for planning.
 *
 * Roster dates are plain `YYYY-MM-DD` keys and shift times are minutes from
 * midnight of that date, in the site's timezone. Rest between shifts is judged
 * in real time, so a shift also needs its instant: `zonedMinuteToUtcMs` turns
 * a wall-clock time into one the way `shift_slot_timestamp` does in Postgres.
 */

export const MINUTES_PER_DAY = 1440;
const MS_PER_DAY = 86_400_000;
const MS_PER_MINUTE = 60_000;

/** Days since the Unix epoch, for date arithmetic without timezones. */
export function toDayNumber(dateKey: string): number {
  const [year, month, day] = dateKey.split('-').map((part) => Number.parseInt(part, 10));
  return Math.round(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

export function fromDayNumber(dayNumber: number): string {
  const date = new Date(dayNumber * MS_PER_DAY);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const addDays = (dateKey: string, days: number): string => fromDayNumber(toDayNumber(dateKey) + days);

/** Every date from `startDate` to `endDate`, both included. */
export function eachDate(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  for (let day = toDayNumber(startDate); day <= toDayNumber(endDate); day += 1) {
    dates.push(fromDayNumber(day));
  }
  return dates;
}

/** The Monday of the week a day falls in. 1970-01-01 was a Thursday. */
export function weekStartDayNumber(dayNumber: number): number {
  const mondayIndex = (((dayNumber + 3) % 7) + 7) % 7;
  return dayNumber - mondayIndex;
}

export const weekStart = (dateKey: string): string => fromDayNumber(weekStartDayNumber(toDayNumber(dateKey)));

export function monthRange(dateKey: string): { startDate: string; endDate: string } {
  const [year, month] = dateKey.split('-').map((part) => Number.parseInt(part, 10));
  const first = Math.round(Date.UTC(year, month - 1, 1) / MS_PER_DAY);
  const last = Math.round(Date.UTC(year, month, 0) / MS_PER_DAY);
  return { startDate: fromDayNumber(first), endDate: fromDayNumber(last) };
}

/** The roster date a minute offset lands on: 1500 on the 3rd is on the 4th. */
export const dateOfMinute = (slotDate: string, minute: number): string => (
  addDays(slotDate, Math.floor(Math.max(minute, 0) / MINUTES_PER_DAY))
);

/** The last date a shift touches; a shift ending at midnight does not touch the next day. */
export const lastDateOfShift = (slotDate: string, endMinute: number): string => (
  dateOfMinute(slotDate, Math.max(endMinute - 1, 0))
);

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

/** How far the zone's wall clock is ahead of UTC at an instant, in milliseconds. */
function zoneOffsetMs(utcMs: number, timeZone: string): number {
  const parts: Record<string, number> = {};
  formatterFor(timeZone).formatToParts(new Date(utcMs)).forEach((part) => {
    if (part.type !== 'literal') parts[part.type] = Number.parseInt(part.value, 10);
  });
  const wallAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return wallAsUtc - (Math.floor(utcMs / 1000) * 1000);
}

/**
 * The instant a wall-clock time falls on in a timezone. Two passes settle the
 * offset on either side of a clock change; an unknown zone reads as UTC.
 */
export function zonedMinuteToUtcMs(slotDate: string, minute: number, timeZone: string): number {
  const wallAsUtc = (toDayNumber(slotDate) * MS_PER_DAY) + (minute * MS_PER_MINUTE);
  try {
    const firstGuess = wallAsUtc - zoneOffsetMs(wallAsUtc, timeZone);
    return wallAsUtc - zoneOffsetMs(firstGuess, timeZone);
  } catch {
    return wallAsUtc;
  }
}
